import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BellRing, Plus, Check, FileText, Download, History, Target, Map, CalendarClock,
  AlertTriangle, Loader2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CareerCard } from "./CareerCard";
import { formatDate, cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

// ---------- ACTIONS TAB (reuses the « À relancer » reminders system) ----------

export function ActionsTab({ playerId }) {
  const [reminders, setReminders] = useState(null);
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/players/${playerId}`);
      setReminders(r.data.reminders || []);
    } catch { setReminders([]); }
  }, [playerId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!note.trim() || !due) return toast.error(tfr("Note et échéance requises"));
    try {
      await api.post("/reminders", { player_id: playerId, due_at: new Date(due).toISOString(), note: note.trim() });
      toast.success(tfr("Action planifiée"));
      setNote(""); setDue("");
      load();
    } catch { toast.error(tfr("Impossible de planifier l'action")); }
  };
  const complete = async (rid) => {
    try {
      await api.patch(`/reminders/${rid}`, { completed: true });
      toast.success(tfr("Action terminée"));
      load();
    } catch { toast.error(tfr("Impossible de terminer l'action")); }
  };

  if (reminders === null) return <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  const open = reminders.filter((r) => !r.completed);
  const today = new Date().toISOString();

  return (
    <CareerCard icon={BellRing} title={tfr("Actions & relances")} subtitle={tfr("Liées au système « À relancer » — aucune donnée dupliquée")} testid="career-actions-tab">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input placeholder={tfr("Ex. Appeler le club pour le point mensuel")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="career-action-note" />
        <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className="sm:w-52 min-w-0" data-testid="career-action-due" />
        <Button onClick={add} className="gap-1 shrink-0" data-testid="career-action-add"><Plus className="h-4 w-4" /> Planifier</Button>
      </div>
      {open.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="career-actions-empty">{tfr("Aucune action en cours pour cet athlète.")}</p>
      ) : (
        <ul className="space-y-2">
          {open.map((r) => {
            const overdue = (r.due_at || "") < today;
            return (
              <li key={r.id} className="flex items-center gap-3 rounded-xl border border-border bg-secondary/30 p-3" data-testid={`career-action-${r.id}`}>
                <span className={cn("h-2 w-2 rounded-full shrink-0", overdue ? "bg-danger" : "bg-info")} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{r.note}</p>
                  <p className={cn("text-[11px]", overdue ? "text-danger font-medium" : "text-muted-foreground")}>
                    {formatDate(r.due_at)}{overdue ? " · En retard" : ""}
                  </p>
                </div>
                <Button variant="outline" size="sm" className="h-7 gap-1 text-xs shrink-0" onClick={() => complete(r.id)} data-testid={`career-action-done-${r.id}`}>
                  <Check className="h-3 w-3" /> Fait
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <Link to="/relances" className="inline-flex items-center gap-1 text-xs text-athlynx hover:underline mt-4">
        <ExternalLink className="h-3 w-3" /> Ouvrir « À relancer »
      </Link>
    </CareerCard>
  );
}

// ---------- DOCUMENTS TAB (reuses player documents) ----------

export function DocumentsTab({ playerId }) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    api.get(`/players/${playerId}/documents`)
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : r.data.documents || []))
      .catch(() => setDocs([]));
  }, [playerId]);

  const download = async (d) => {
    try {
      const r = await api.get(`/players/${playerId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = d.filename || d.label || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(tfr("Téléchargement impossible")); }
  };

  if (docs === null) return <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10);

  return (
    <CareerCard icon={FileText} title={tfr("Documents du dossier")} subtitle={tfr("Documents de l'athlète — gérés depuis la fiche joueur")} testid="career-documents-tab">
      {docs.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="career-docs-empty">{tfr("Aucun document dans le dossier.")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {docs.map((d) => {
            const exp = d.expires_at ? String(d.expires_at).slice(0, 10) : null;
            const expired = exp && exp < today;
            const expSoon = exp && !expired && exp <= soon;
            return (
              <li key={d.id} className="flex items-center gap-3 py-2.5" data-testid={`career-doc-${d.id}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{d.label || d.filename}</p>
                  <p className="text-[11px] text-muted-foreground">{d.doc_type || "Document"}{exp ? ` · Expire le ${formatDate(exp)}` : ""}</p>
                </div>
                {expired && <Badge variant="outline" className="text-[10px] border-danger/40 text-danger">{tfr("Expiré")}</Badge>}
                {expSoon && <Badge variant="outline" className="text-[10px] border-warning/40 text-warning">{tfr("Expire bientôt")}</Badge>}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => download(d)} aria-label={tfr("Télécharger")} data-testid={`career-doc-dl-${d.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      <Link to={`/players/${playerId}`} className="inline-flex items-center gap-1 text-xs text-athlynx hover:underline mt-4">
        <ExternalLink className="h-3 w-3" /> Gérer les documents sur la fiche athlète
      </Link>
    </CareerCard>
  );
}

// ---------- HISTORY TAB ----------

const HISTORY_LABELS = {
  CAREER_PLAN_CREATED: { label: tfr("Plan de carrière créé"), Icon: Map },
  CAREER_PLAN_EXPORTED: { label: tfr("Plan exporté en PDF"), Icon: FileText },
  CAREER_MILESTONE_ADDED: { label: tfr("Jalon ajouté"), Icon: Map },
  CAREER_MILESTONE_UPDATED: { label: tfr("Jalon mis à jour"), Icon: Map },
  CAREER_MILESTONE_DELETED: { label: tfr("Jalon supprimé"), Icon: Map },
  CAREER_GOAL_ADDED: { label: tfr("Objectif ajouté"), Icon: Target },
  CAREER_GOAL_UPDATED: { label: tfr("Objectif mis à jour"), Icon: Target },
  CAREER_GOAL_COMPLETED: { label: tfr("Objectif terminé"), Icon: Check },
  CAREER_GOAL_DELETED: { label: tfr("Objectif supprimé"), Icon: Target },
  CAREER_BLOCKER_STATE: { label: tfr("Point de vigilance traité"), Icon: AlertTriangle },
  CAREER_MEETING_PREPARED: { label: tfr("Réunion préparée"), Icon: CalendarClock },
};

export function HistoryTab({ playerId }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get(`/players/${playerId}/career-plan/history`)
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, [playerId]);

  if (events === null) return <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <CareerCard icon={History} title={tfr("Historique du plan")} subtitle={tfr("Toutes les évolutions du Career Development Plan")} testid="career-history-tab">
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="career-history-empty">
          {tfr("L'historique apparaîtra ici au fil des actions sur le plan.")}
        </p>
      ) : (
        <ul className="space-y-1">
          {events.map((e) => {
            const meta = HISTORY_LABELS[e.action_type] || { label: e.action_type, Icon: History };
            const detail = e.metadata?.title || e.metadata?.meeting_type || e.metadata?.key || "";
            return (
              <li key={e.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0" data-testid={`history-event-${e.id}`}>
                <span className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <meta.Icon className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{meta.label}{detail ? <span className="text-muted-foreground"> — {detail}</span> : null}</p>
                </div>
                <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{formatDate(e.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </CareerCard>
  );
}
